import { useState, useCallback, useRef, DragEvent } from 'react';
import { Upload, X, FileArchive, AlertCircle, CheckCircle } from 'lucide-react';
import clsx from 'clsx';
import { parseChapterNumber, type ParsedFile, sortByChapterNumber } from '../utils/parseChapterNumber';
import Button from './Button';
import Input from './Input';

interface ChapterDropZoneProps {
  onFilesSelected: (files: ParsedFile[]) => void;
  disabled?: boolean;
}

export default function ChapterDropZone({ onFilesSelected, disabled }: ChapterDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<ParsedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const processFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: ParsedFile[] = Array.from(fileList).map((file) => {
      const chapterNo = parseChapterNumber(file.name);
      return {
        file,
        filename: file.name,
        chapterNo,
        isValid: chapterNo !== null,
      };
    });

    const sorted = sortByChapterNumber(newFiles);
    setFiles(sorted);
    onFilesSelected(sorted);
  }, [onFilesSelected]);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  }, [disabled, processFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      processFiles(selectedFiles);
    }
  }, [processFiles]);

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const handleChapterNoChange = (index: number, value: string) => {
    const num = parseFloat(value);
    const newFiles = [...files];
    newFiles[index] = {
      ...newFiles[index],
      chapterNo: isNaN(num) || num <= 0 ? null : num,
      isValid: !isNaN(num) && num > 0,
    };
    setFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const handleClearAll = () => {
    setFiles([]);
    onFilesSelected([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const allValid = files.every((f) => f.isValid);
  const invalidCount = files.filter((f) => !f.isValid).length;

  return (
    <div className="space-y-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={clsx(
          'relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer',
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".zip,.cbz,.rar,.cbr,.7z"
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        <Upload
          className={clsx(
            'mx-auto h-12 w-12 mb-4',
            isDragging ? 'text-primary-500' : 'text-gray-400'
          )}
        />

        <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
          {isDragging ? 'Отпустите файлы здесь' : 'Перетащите архивы глав'}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          или нажмите для выбора файлов
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Поддерживаются: ZIP, CBZ, RAR, CBR, 7Z
        </p>
      </div>

      {files.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                Файлы для загрузки ({files.length})
              </span>
              {invalidCount > 0 && (
                <span className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4" />
                  {invalidCount} без номера
                </span>
              )}
              {allValid && files.length > 0 && (
                <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle className="h-4 w-4" />
                  Все номера определены
                </span>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={handleClearAll}>
              Очистить
            </Button>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={`${file.filename}-${index}`}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3',
                  !file.isValid && 'bg-amber-50 dark:bg-amber-900/10'
                )}
              >
                <FileArchive className="h-6 w-6 flex-shrink-0 text-gray-400" />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.filename}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    Глава:
                  </label>
                  <Input
                    type="number"
                    value={file.chapterNo?.toString() ?? ''}
                    onChange={(e) => handleChapterNoChange(index, e.target.value)}
                    placeholder="?"
                    className={clsx(
                      'w-20 text-center',
                      !file.isValid && 'border-amber-500 focus:ring-amber-500'
                    )}
                    min={1}
                    step="0.5"
                  />
                </div>

                <button
                  onClick={() => handleRemoveFile(index)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  title="Удалить"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
